import {
  LiveKitRoom,
  RoomAudioRenderer,
  useIsMuted,
  useLocalParticipant,
  useMediaDeviceSelect,
  useRemoteParticipants,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import { useGrippContext } from '@gripp/shared-logic';
import { Icon, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

interface CallRoomProps {
  url: string;
  token: string;
  /** Name of the other party, shown in the call header. */
  peerName: string;
  /** Caller is shown a "ringing" hint until the callee joins. */
  role: 'caller' | 'callee';
  /** Fired when the local user leaves or the connection drops. */
  onDisconnected: () => void;
}

/**
 * Full-screen in-call overlay (web). Standard 1:1 layout: remote participant
 * fills the screen, the local camera sits in a tap-to-swap picture-in-picture,
 * header + control bar float over the video. Built from `@livekit/components-react`
 * primitives rather than the `<VideoConference>` prefab — the prefab's control bar
 * depends on CSS that is unreliable inside the react-native-web `<Modal>`, so a
 * hand-built bar (mic / camera / hang-up) is the dependable choice and gives
 * parity with the RN app. `<RoomAudioRenderer>` plays remote audio (the prefab
 * used to provide this). Leaving fires onDisconnected.
 */
export const CallRoom = ({
  url,
  token,
  peerName,
  role,
  onDisconnected,
}: CallRoomProps) => {
  return (
    <Modal visible animationType="fade" onRequestClose={onDisconnected}>
      <View style={styles.container}>
        <LiveKitRoom
          serverUrl={url}
          token={token}
          connect
          video
          audio
          onDisconnected={onDisconnected}
          style={{ height: '100%', width: '100%' }}
        >
          <RoomAudioRenderer />
          <Stage />
          <CallHeader peerName={peerName} role={role} />
          <ControlBar onHangUp={onDisconnected} />
        </LiveKitRoom>
      </View>
    </Modal>
  );
};

/**
 * Peer name + a "Ringing…" hint shown to the caller ONLY until a remote
 * participant joins. Presence-driven (`useRemoteParticipants`) rather than
 * call-state-driven, so it clears the moment the callee answers even if the
 * caller's `updatedCall` event is delayed. `pointerEvents="none"` so it never
 * swallows clicks meant for the video.
 */
const CallHeader = ({
  peerName,
  role,
}: {
  peerName: string;
  role: 'caller' | 'callee';
}) => {
  const remoteParticipants = useRemoteParticipants();
  const ringing = role === 'caller' && remoteParticipants.length === 0;

  return (
    <View style={styles.header} pointerEvents="none">
      <Text style={styles.peerName} numberOfLines={1}>
        {peerName}
      </Text>
      {ringing && <Text style={styles.subtitle}>Ringing…</Text>}
    </View>
  );
};

/**
 * Fills its parent with a participant's camera; mirrors local (self-view).
 *
 * When the camera is off (track muted) the underlying `<video>` element freezes
 * on its last decoded frame rather than clearing, so a disabled self-view would
 * otherwise show a frozen still. `useIsMuted` lets us render a plain black fill
 * instead — for the local self-view when you turn your camera off, and for the
 * remote tile when the other party turns theirs off.
 */
const FillVideo = ({
  trackRef,
  mirror,
}: {
  trackRef: NonNullable<Parameters<typeof VideoTrack>[0]['trackRef']>;
  mirror: boolean;
}) => {
  const isMuted = useIsMuted(trackRef);

  if (isMuted) {
    return <View style={styles.cameraOff} />;
  }

  return (
    <VideoTrack
      trackRef={trackRef}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: mirror ? 'scaleX(-1)' : undefined,
      }}
    />
  );
};

/**
 * Video stage: remote camera full-screen with the local camera in a tap-to-swap
 * PiP. Before the remote joins, the local camera fills the screen and no PiP is
 * shown. (Web has no SurfaceView z-order issue — DOM stacking via zIndex.)
 */
const Stage = () => {
  const tracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const [swapped, setSwapped] = useState(false);

  const localTrack = tracks.find((t) => t.participant.isLocal);
  const remoteTrack = tracks.find((t) => !t.participant.isLocal);

  if (!remoteTrack) {
    return (
      <View style={styles.stage}>
        {localTrack ? (
          <FillVideo trackRef={localTrack} mirror />
        ) : (
          <Text style={styles.waiting}>Connecting…</Text>
        )}
      </View>
    );
  }

  const bigTrack = swapped ? localTrack : remoteTrack;
  const pipTrack = swapped ? remoteTrack : localTrack;

  return (
    <View style={styles.stage}>
      {bigTrack && (
        <FillVideo trackRef={bigTrack} mirror={bigTrack.participant.isLocal} />
      )}
      {pipTrack && (
        <Pressable
          style={styles.pip}
          onPress={() => setSwapped((s) => !s)}
          accessibilityRole="button"
          accessibilityLabel="Swap video"
        >
          <FillVideo trackRef={pipTrack} mirror={pipTrack.participant.isLocal} />
        </Pressable>
      )}
    </View>
  );
};

interface ControlBarProps {
  onHangUp: () => void;
}

/**
 * In-call controls: toggle mic, toggle camera, hang up — parity with the RN app.
 */
const ControlBar = ({ onHangUp }: ControlBarProps) => {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } =
    useLocalParticipant();
  const { logger } = useGrippContext();

  const toggleMic = () => {
    localParticipant
      .setMicrophoneEnabled(!isMicrophoneEnabled)
      .catch((err) => logger?.error(err));
  };
  const toggleCamera = () => {
    localParticipant
      .setCameraEnabled(!isCameraEnabled)
      .catch((err) => logger?.error(err));
  };

  return (
    <View style={styles.controlBar}>
      <ControlButton
        iconName="Microphone"
        label={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
        active={isMicrophoneEnabled}
        onPress={toggleMic}
      />
      <ControlButton
        iconName="Camera"
        label={isCameraEnabled ? 'Turn camera off' : 'Turn camera on'}
        active={isCameraEnabled}
        onPress={toggleCamera}
      />
      <CameraSwitcher />
      <ControlButton
        iconName="Phone"
        label="Hang up"
        variant="danger"
        active
        onPress={onHangUp}
      />
    </View>
  );
};

/**
 * Desktop camera picker (Slack-style): a control button that opens a small
 * popover listing the available video inputs, with the active one highlighted.
 * Rendered only when there is more than one camera to choose from, so a laptop
 * with a single webcam shows nothing. `useMediaDeviceSelect` handles enumeration
 * + switching the live LiveKit publication to the chosen device.
 */
const CameraSwitcher = () => {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect(
    { kind: 'videoinput' }
  );
  const { logger } = useGrippContext();
  const [open, setOpen] = useState(false);

  if (devices.length <= 1) {
    return null;
  }

  const selectDevice = (deviceId: string) => {
    setOpen(false);
    setActiveMediaDevice(deviceId).catch((err) => logger?.error(err));
  };

  return (
    <View style={styles.switcherWrap}>
      {open && (
        <>
          {/* Full-screen catcher so a click anywhere else dismisses the menu. */}
          <Pressable
            style={styles.switcherBackdrop}
            onPress={() => setOpen(false)}
            accessibilityRole="button"
            accessibilityLabel="Close camera picker"
          />
          <View style={styles.deviceMenu}>
            {devices.map((device, index) => {
              const isActive = device.deviceId === activeDeviceId;
              const label = device.label || `Camera ${index + 1}`;
              return (
                <Pressable
                  key={device.deviceId}
                  onPress={() => selectDevice(device.deviceId)}
                  accessibilityRole="button"
                  accessibilityLabel={`Use ${label}`}
                  accessibilityState={{ selected: isActive }}
                  style={styles.deviceItem}
                >
                  <Text
                    style={[
                      styles.deviceLabel,
                      isActive && styles.deviceLabelActive,
                    ]}
                    numberOfLines={1}
                  >
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      )}
      <ControlButton
        iconName="Reset"
        label="Switch camera"
        active
        onPress={() => setOpen((o) => !o)}
      />
    </View>
  );
};

interface ControlButtonProps {
  iconName: 'Microphone' | 'Camera' | 'Phone' | 'Reset';
  label: string;
  active: boolean;
  variant?: 'neutral' | 'danger';
  onPress: () => void;
}

const ControlButton = ({
  iconName,
  label,
  active,
  variant = 'neutral',
  onPress,
}: ControlButtonProps) => {
  const containerStyle: ViewStyle = {
    ...styles.controlButton,
    backgroundColor:
      variant === 'danger'
        ? tokens.colors.semantic.error
        : active
        ? tokens.colors.brand.white
        : 'rgba(255, 255, 255, 0.25)',
  };
  const iconColor =
    variant === 'danger'
      ? tokens.colors.brand.white
      : active
      ? tokens.colors.neutral[800]
      : tokens.colors.brand.white;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={containerStyle}
    >
      <Icon name={iconName} size={26} color={iconColor} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: tokens.colors.brand.black,
  },
  stage: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.black,
  },
  // Shown in place of a camera tile when that camera is off — a plain black fill
  // instead of the frozen last frame the paused <video> would otherwise show.
  cameraOff: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colors.brand.black,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  peerName: {
    ...tokens.typography.title.medium,
    color: tokens.colors.brand.white,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  pip: {
    position: 'absolute',
    right: tokens.spacing.lg,
    bottom: 110,
    zIndex: 20,
    width: 160,
    height: 220,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: tokens.colors.brand.black,
    overflow: 'hidden',
  },
  waiting: {
    flex: 1,
    color: tokens.colors.brand.white,
    textAlign: 'center',
    ...tokens.typography.body.medium,
  },
  controlBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: tokens.spacing.xl,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing['2xl'],
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  controlButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Anchor for the camera-picker popover so the menu floats directly above the
  // switch-camera button.
  switcherWrap: {
    position: 'relative',
  },
  // Invisible full-screen catcher: a click outside the open menu closes it.
  // RN-Web honors position:'fixed' (viewport-anchored, so it covers the whole
  // call overlay rather than just the tiny switcher wrapper), but RN's style
  // types stop at 'absolute' — cast to keep the viewport-anchored behaviour.
  switcherBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
  } as unknown as ViewStyle,
  deviceMenu: {
    position: 'absolute',
    bottom: 56 + tokens.spacing.sm,
    left: '50%',
    transform: [{ translateX: -120 }],
    width: 240,
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: tokens.borderRadius.md,
    paddingVertical: tokens.spacing.xs,
    zIndex: 30,
  },
  deviceItem: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
  },
  deviceLabel: {
    ...tokens.typography.body.small,
    color: tokens.colors.neutral[300],
  },
  deviceLabelActive: {
    color: tokens.colors.brand.white,
    ...tokens.typography.body.smallEmphasised,
  },
});
